import { Box, Divider, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { WithContext as ReactTags } from 'react-tag-input'

import RootButton from '../../Buttons/RootButton'

interface RemoveComponentProps {
  className: string
  onRemove: () => void
}

const RemovableIcon: React.FC<RemoveComponentProps> = ({ className, onRemove }) => (
  <button
    type="button"
    onClick={onRemove}
    className={className}
    style={{ padding: '7px', height: '30px', width: '35px' }}
    aria-label="Remove tag"
  >
    <IconX size={16} />
  </button>
)

const KeyCodes = {
  comma: 188,
  enter: 13,
  space: 32,
}

const delimiters = [KeyCodes.comma, KeyCodes.enter, KeyCodes.space]

interface TagsProps {
  tags: DeepReadonly<string[]>
  setTags: (tags: string[]) => void
  prevStep: () => void
  nextStep: () => void
  metadata: any
  saveDraft: () => void
}

const Tags: React.FC<TagsProps> = ({ tags, setTags, prevStep, nextStep, metadata, saveDraft }) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()

  const tagsRules = metadata.tags.rules
  const { max: maxSize } = tagsRules
  const { max: maxChars } = tagsRules.tagSize

  // Copy tags to auxTags
  const [auxTags, setAuxTags] = useState<DeepReadonly<{ id: string; text: string }[]>>([])
  useEffect(() => {
    let tgs: { id: string; text: string }[] = []
    Object.entries(tags).forEach(([key, value]) => {
      tgs = [
        ...tgs,
        {
          id: key + 1,
          text: value,
        },
      ]
    })
    setAuxTags(tgs)
  }, [tags])

  // Update auxTags
  const handleDelete = (i: number) => {
    setAuxTags(auxTags.filter((tag, index) => index !== i))
    setTags(tags.filter((tag, index) => index !== i))
  }
  const handleAddition = (tag: { id: string; text: string }) => {
    if (auxTags.length < maxSize) {
      setAuxTags([...auxTags, tag])
      setTags([...tags, tag.text])
    }
  }

  // Continue
  const next = useCallback(() => {
    saveDraft()
    nextStep()
  }, [])

  return (
    <Stack>
      <Divider />
      <Stack style={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
        <Title order={3}>{t('components.studio.tags.title', 'Input Tags')}</Title>
        {/* <Text
          style={{
            color: theme.colors.textAndIcon[0],
            fontSize: '14px',
            fontFamily: 'monument-grotesk-regularitalic',
          }}
        >
          {t('components.studio.required')}
        </Text> */}
      </Stack>
      <Trans i18nKey="components.studio.tags.description">
        <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}>
          Tags help describe your content’s purpose, interoperability, and discoverability when
          distributed into Spaces.
        </Text>
      </Trans>
      <Stack style={{ textAlign: 'left' }}>
        <Text>
          <Trans i18nKey="components.studio.tags.maxTagsMessage">
            Add up to {{ max: maxSize }} tags. Each tag can be up to {{ maxChars }} characters.
          </Trans>
        </Text>
        {tags.length >= maxSize && (
          <Text>
            <Trans i18nKey="components.studio.tags.maxTagsReached">
              You have reached the limit of tags (max {{ max: maxSize }})
            </Trans>
          </Text>
        )}
        <Box
          style={{
            border: `2px solid ${theme.colors.textAndIcon[0]}`,
            borderRadius: '8px',
            minHeight: '80px',
            height: 'fit-content',
            padding: '10px',
          }}
        >
          {/* TODO: tags and handleAddition typed as any to prevent error. Update or change to mantine tags */}
          <ReactTags
            tags={auxTags as DeepMutable<typeof auxTags> as any}
            delimiters={delimiters}
            handleDelete={handleDelete}
            handleAddition={handleAddition as any}
            allowAdditionFromPaste={false}
            inputFieldPosition="top"
            placeholder="Type and press enter to add new tag"
            maxLength={maxChars}
            removeComponent={RemovableIcon}
          />
        </Box>
      </Stack>
      <Stack style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
        <RootButton secondary onClick={prevStep}>
          {t('buttons.previous')}
        </RootButton>
        <RootButton onClick={next}>{t('buttons.continue')}</RootButton>
      </Stack>
    </Stack>
  )
}
export default Tags
